import React from 'react';
import Grid from '@mui/material/GridLegacy';
import { Box, Typography, Button, Card, CardContent, Chip } from '@mui/material';
import { Add, Edit, Delete, Campaign } from '@mui/icons-material';
import { getImageDisplayUrl } from '../../../config/api';

export default function BannersTab({
  banners = [],
  categories = [],
  onAddBanner,
  onEditBanner,
  onDeleteBanner,
}) {
  const isBannerCurrentlyActive = (banner) => {
    const now = new Date();
    const isEnabled = banner?.isActive !== false;
    const hasStarted = !banner?.startDate || new Date(banner.startDate) <= now;
    const isNotExpired = !banner?.endDate || new Date(banner.endDate) >= now;
    return isEnabled && hasStarted && isNotExpired;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
          Banner Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={onAddBanner}
          sx={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            boxShadow: '0 4px 15px rgba(240, 147, 251, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
              boxShadow: '0 6px 20px rgba(240, 147, 251, 0.5)',
              transform: 'translateY(-2px)'
            },
            transition: 'all 0.3s ease'
          }}
        >
          Create Banner
        </Button>
      </Box>

      {banners.length === 0 ? (
        <Card sx={{
          p: 6,
          textAlign: 'center',
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.05)'
        }}>
          <Campaign sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No banners yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click "Create Banner" to get started
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {banners.map((banner) => (
            <Grid item xs={12} sm={6} md={4} key={banner._id}>
              <Card sx={{
                height: '100%',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                border: '1px solid rgba(0, 0, 0, 0.05)',
                transition: 'all 0.3s ease',
                overflow: 'hidden',
                background: banner.imageUrl ? 'transparent' : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
                }
              }}>
                {banner.imageUrl && (
                  <Box sx={{
                    width: '100%',
                    height: 180,
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <img
                      src={getImageDisplayUrl(banner.imageUrl)}
                      alt={banner.title}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                      <Chip
                        label={isBannerCurrentlyActive(banner) ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          bgcolor: isBannerCurrentlyActive(banner) ? 'rgba(46, 125, 50, 0.9)' : 'rgba(158, 158, 158, 0.9)',
                          color: 'white',
                          fontWeight: 600,
                          backdropFilter: 'blur(10px)'
                        }}
                      />
                    </Box>
                  </Box>
                )}
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {banner.title}
                      </Typography>
                      {banner.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {banner.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    <Chip
                      label={banner.featureType}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(79, 172, 254, 0.1)',
                        color: 'info.main',
                        fontWeight: 500,
                        textTransform: 'capitalize'
                      }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Target:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {banner.isActive === false ? (
                        'None (inactive)'
                      ) : banner.featureType === 'place' ? (
                        typeof banner.targetId === 'object' && banner.targetId?.name
                          ? banner.targetId.name
                          : 'Place'
                      ) : banner.featureType === 'category' ? (
                        categories.find(c => c._id === banner.targetId || c.name === banner.targetId)?.name ||
                        (typeof banner.targetId === 'string' ? banner.targetId : 'Category')
                      ) : banner.featureType === 'topic' ? (
                        typeof banner.targetId === 'string' ? banner.targetId : 'Topic'
                      ) : 'N/A'}
                    </Typography>
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    p: 2,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, rgba(240, 147, 251, 0.1) 0%, rgba(245, 87, 108, 0.1) 100%)',
                    border: '1px solid rgba(240, 147, 251, 0.2)',
                    mb: 2
                  }}>
                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {banner.clickCount || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Clicks</Typography>
                    </Box>
                    <Box sx={{ width: '1px', bgcolor: 'rgba(240, 147, 251, 0.3)', mx: 1 }} />
                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        {banner.viewCount || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Views</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => onEditBanner(banner)}
                      sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => onDeleteBanner(banner)}
                      sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontWeight: 500 }}
                    >
                      Delete
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
